import React from 'react';
import SortTh from '../../components/SortTh';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import { fmtCurrency } from '../../lib/display-labels';
import { fieldHoursSourceLabel, hrs, readCrewHoursRow, type CrewHoursRow } from '../../lib/reports/crew-hours';

/**
 * Hours & Jobs by Employee (api#1193): Field hrs, Shop hrs and Total hrs, each
 * the API's (Total is field + shop, added server-side). Clicking an employee
 * opens their own page — jobs and shop shifts over the same range.
 *
 * It replaces the runner's generic one-column-per-key table for this report,
 * which would have printed `field_hours_source`, `pay_reason` and the pay note
 * as raw columns and lost the money format on `pay` (renamed from
 * `pay_earned`).
 *
 * The row is the target and holds no buttons, so it carries role="button" —
 * the Quotes shape, not the Rate Sheets exception.
 */

const sc = '!text-[10px] !tracking-wide !py-1 !px-2';
const scR = `${sc} !text-right`;
const cell = 'px-2 py-2 text-[11px] text-foreground';
const sub = 'block text-[10px] text-fieldiq-muted';

export interface CrewHoursTableProps {
  /** Raw API rows, already sorted by the runner's shared sort. */
  rows: any[];
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  onOpen: (userId: string) => void;
}

export function CrewHoursTable({ rows, sortBy, sortDir, onSort, onOpen }: CrewHoursTableProps) {
  const read = rows.map(readCrewHoursRow);
  // Pay is ABSENT for a role the API strips it from — no column, not a column of dashes.
  const withPay = read.some((r) => r.pay !== undefined);
  const payNote = read.find((r) => r.payNote)?.payNote ?? null;
  const th = (label: string, field: string, right = true) => (
    <SortTh label={label} field={field} sortBy={sortBy} sortDir={sortDir} onSort={onSort} className={right ? scR : sc} />
  );
  const open = (r: CrewHoursRow) => { if (r.userId) onOpen(r.userId); };
  const keys = (r: CrewHoursRow) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(r); }
  };

  return (
    <div data-testid="crew-hours-table" className="space-y-2">
      <p className="text-[11px] text-fieldiq-muted">Select an employee to see their jobs and shop shifts.</p>

      {/* md and up: the table. */}
      <div className="hidden md:block rounded-lg border border-fieldiq-border overflow-x-auto">
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow className="bg-fieldiq-card/50">
              {th('Employee', 'name', false)}
              {th('Jobs', 'jobs_worked')}
              {th('Days', 'days_worked')}
              {th('Field hrs', 'field_hours')}
              {th('Shop hrs', 'shop_hours')}
              {th('Total hrs', 'total_hours')}
              {withPay && th('Pay', 'pay')}
            </TableRow>
          </TableHeader>
          <TableBody>
            {read.map((r, i) => (
              <TableRow
                key={r.userId ?? i}
                data-testid="crew-hours-row"
                role="button"
                tabIndex={r.userId ? 0 : undefined}
                aria-label={r.userId ? `Open ${r.name ?? 'employee'}'s hours` : undefined}
                onClick={() => open(r)}
                onKeyDown={keys(r)}
                className="cursor-pointer border-fieldiq-border outline-none hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
              >
                <TableCell className={cell}>
                  <span className="font-semibold">{r.name ?? '—'}</span>
                  {r.title && <span className={sub}>{r.title}</span>}
                </TableCell>
                <TableCell className={`${cell} text-right tabular-nums`}>{r.jobsWorked ?? '—'}</TableCell>
                <TableCell className={`${cell} text-right tabular-nums`}>{r.daysWorked ?? '—'}</TableCell>
                <TableCell className={`${cell} text-right tabular-nums`}>
                  {hrs(r.fieldHours)}
                  {/* One line: where the hours came from, and the multi-day
                      tickets the API could not count (no per-day hours). */}
                  <span data-testid="crew-hours-field-sub" className={sub}>
                    {[
                      fieldHoursSourceLabel(r.fieldHoursSource),
                      r.unresolvedTicketCount ? `+${r.unresolvedTicketCount} multi-day not counted` : null,
                    ].filter(Boolean).join(' · ')}
                  </span>
                </TableCell>
                <TableCell className={`${cell} text-right tabular-nums`}>
                  {hrs(r.shopHours)}
                  {r.unapprovedShopHours !== null && r.unapprovedShopHours > 0 && (
                    <span className={sub}>{`${hrs(r.unapprovedShopHours)} unapproved`}</span>
                  )}
                </TableCell>
                <TableCell className={`${cell} text-right tabular-nums font-semibold`}>{hrs(r.totalHours)}</TableCell>
                {withPay && (
                  <TableCell className={`${cell} text-right tabular-nums`}>
                    {fmtCurrency(r.pay ?? null)}
                    {r.pay == null && r.payReason && <span className={`${sub} max-w-[180px] ml-auto whitespace-normal`}>{r.payReason}</span>}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Below md: the same rows as stacked cards. */}
      <ul className="md:hidden space-y-2">
        {read.map((r, i) => (
          <li key={r.userId ?? i}>
            <button
              type="button"
              data-testid="crew-hours-card"
              onClick={() => open(r)}
              className="w-full rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-2.5 text-left outline-none focus-visible:bg-fi-amber/10"
            >
              <span className="block text-sm font-semibold text-fieldiq-text">{r.name ?? '—'}</span>
              {r.title && <span className={sub}>{r.title}</span>}
              <span className="mt-1.5 grid grid-cols-3 gap-2 text-[11px] tabular-nums text-fieldiq-text">
                <span><span className={sub}>Field</span>{hrs(r.fieldHours)}</span>
                <span><span className={sub}>Shop</span>{hrs(r.shopHours)}</span>
                <span className="font-semibold"><span className={sub}>Total</span>{hrs(r.totalHours)}</span>
              </span>
              <span className="mt-1 block text-[10px] text-fieldiq-muted">
                {[
                  r.jobsWorked !== null ? `${r.jobsWorked} jobs` : null,
                  fieldHoursSourceLabel(r.fieldHoursSource),
                  r.unapprovedShopHours ? `${hrs(r.unapprovedShopHours)} shop unapproved` : null,
                  withPay ? `Pay ${fmtCurrency(r.pay ?? null)}` : null,
                ].filter(Boolean).join(' · ')}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {withPay && payNote && <p data-testid="crew-hours-pay-note" className="text-[11px] text-fieldiq-muted">Pay: {payNote.charAt(0).toLowerCase() + payNote.slice(1)}.</p>}
    </div>
  );
}

export default CrewHoursTable;
